import {DataObjectId, GroupChat, Hub, SandboxTree} from 'yougile-model'
import {UserSettings} from '@client/user-settings'
import {newMentionsFor} from '@extensions/chat'
import {ChatUtils} from '../chat-utils'
import {globals} from '../globals-base'
import {ChatProxy} from './chat-proxy'
import {UserProxy} from './user-proxy'

export type GroupChatProxy = ChatProxy & {
  users: Map<DataObjectId, UserProxy & {isNotified: boolean}>
  starred?: boolean
}

type GroupChatProxyOptions = {
  ignoreUnreadIfNotNotified?: boolean
}

export const GroupChatProxy = {
  create(
    box: SandboxTree,
    groupChatHub: Hub,
    options: GroupChatProxyOptions = {}
  ): GroupChatProxy {
    const users: Map<DataObjectId, UserProxy & {isNotified: boolean}> =
      new Map()
    const participants = GroupChat.getParticipantsWithNotified(groupChatHub)
    const companyUsers = box.getAllUsers(false)
    Object.keys(participants).forEach((uid: DataObjectId) => {
      const user = companyUsers.find(u => u.id === uid)

      if (user) {
        users.set(uid, {
          ...UserProxy.create(user),
          isNotified: participants[uid].notified
        })
      }
    })

    const title =
      GroupChat.getTitle(groupChatHub) ||
      [...users.values()]
        .sort(
          (a, b) =>
            +(a.userId === globals.currentUserId) -
            +(b.userId === globals.currentUserId)
        )
        .map(u => u.userName)
        .join(', ')

    const lastMessage = groupChatHub.chat.lastMessage()
    const lastMessageFromMe = groupChatHub.chat.lastMessage({
      userId: globals.currentUserId
    })

    const chatId = groupChatHub.id
    const companyId = box.companyId
    const starred = UserSettings.isStarred({id: chatId, companyId})

    let newMessagesAmount = 0
    let shouldIgnoreUnread = false

    if (options.ignoreUnreadIfNotNotified) {
      const user = users.get(globals.currentUserId)
      if (
        user &&
        !user.isNotified &&
        !ChatUtils.chatHasUnseenMention(groupChatHub)
      ) {
        shouldIgnoreUnread = true
      }
    }

    if (!shouldIgnoreUnread) {
      newMessagesAmount = groupChatHub.chat.newMessagesFor(
        globals.currentUserId
      )
    }

    const chat: ChatProxy = {
      chatId: groupChatHub.id,
      chatName: title,
      chatType: 'group',
      createdTimestamp: groupChatHub.timestamp,
      locationPath: '',
      messagesCount: groupChatHub.chat.length,
      newMessagesAmount,
      getNewMentionsAmount: startFrom =>
        newMentionsFor(globals.currentUserId, startFrom, chatId),
      lastMessageText: lastMessage ? lastMessage.text : '',
      lastMessageAuthor: lastMessage
        ? ChatUtils.getUserRealNameForDisplay(lastMessage.from, groupChatHub)
        : '',
      lastMessageTimestamp: lastMessage ? lastMessage.timestamp : 0,
      lastMessageId: lastMessage ? lastMessage.id : undefined,
      markImportant:
        groupChatHub.data?.markImportantMap?.[globals.currentUserId],
      markAsSeen: () => ChatUtils.markAsSeen(groupChatHub),
      lastMessageFromMeTimestamp: lastMessageFromMe?.timestamp
    }

    return {
      users,
      ...chat,
      starred
    }
  }
}
